import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  InputLabel,
} from '@mui/material';

// NETWORK
import useErrorMessagesRequest from '../../../../requests/ErrorMessagesRequest';

// COMPONENTS
import LanguagePicker from '../../../common/LanguagePicker';
import ProviderPicker from '../../../common/ProviderPicker';
import EditModal from './ErrorMessagesEditModal';
import Paginator from '../../../common/Paginator';

// CONFIG
import config from '../../../../Config';
import Loader from '../../../common/Loader';

const ErrorMessages = (props) => {
  const { errorHandler } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessages, setErrorMessages] = useState([]);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [count, setCount] = useState(1);
  const [page, setPage] = useState([]);

  const [providerId, setProviderId] = useState(1);
  const [languageId, setLanguageId] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedErrorMessage, setSelectedErrorMessage] = useState(null);
  const { getErrorMessages } = useErrorMessagesRequest();

  useEffect(() => {
    fetchErrorMessages();
  }, [providerId, languageId]);

  useEffect(() => {
    updatePage(errorMessages);
  }, [pageNumber, pageLength]);

  useEffect(() => {
    if (selectedErrorMessage) {
      setShowEditModal(true);
    }
  }, [selectedErrorMessage]);

  const fetchErrorMessages = async () => {
    setIsLoading(true);
    const messages = await getErrorMessages(
      providerId,
      languageId,
      errorHandler
    );
    messages.forEach((message) => {
      message.fallback = renderFallback(message);
    });
    setCount(messages.length);
    updatePage(messages);
    setErrorMessages(messages);
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  };

  const renderFallback = (errorMessage) => {
    if (errorMessage.provider_id === providerId) {
      return '';
    }

    if (errorMessage.provider_id === 1) {
      return 'R';
    }

    return '0';
  };

  const updatePage = (messages) => {
    setPage(
      messages.slice(
        (pageNumber - 1) * pageLength,
        (pageNumber - 1) * pageLength + pageLength
      )
    );
  };

  const errorMessageDetails = (errorMessage) => {
    const message = { ...errorMessage };

    if (message.provider_id !== providerId) {
      message.error_messages_id = null;
      message.provider_id = providerId;
    }

    if (message.language_id !== languageId) {
      message.error_messages_id = null;
      message.language_id = languageId;
    }

    setSelectedErrorMessage(message);
  };

  const handleAdd = () => {
    setSelectedErrorMessage({
      provider_id: providerId,
      language_id: languageId,
    });
  };

  const handleSave = () => {
    fetchErrorMessages();
    setShowEditModal(false);
  };

  const handleProviderChange = (e, newValue) => {
    if (newValue) {
      setProviderId(parseInt(newValue.id));
    }
  };

  const handleLanguageChange = (value) => {
    setLanguageId(parseInt(value));
  };

  const changePageLength = (event) => {
    const value = event.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  const changeEvent = (value) => {
    setPageNumber(value);
  };

  return (
    <Box key="ErrorMessages">
      <Stack
        alignItems={{ md: 'end' }}
        justifyContent="space-between"
        marginBottom={2}
        flexDirection={{ md: 'row' }}
        gap={2}
      >
        <Box display="flex" gap={2}>
          <Box sx={{ minWidth: 250 }}>
            <InputLabel>Provider</InputLabel>
            <ProviderPicker
              providerId={providerId}
              onChange={handleProviderChange}
              fullWidth
            />
          </Box>

          <Box>
            <InputLabel>Language</InputLabel>
            <LanguagePicker
              selectedLanguageId={languageId}
              onChange={(value) => handleLanguageChange(value)}
            />
          </Box>
        </Box>

        <Box>
          <Button variant="contained" color="secondary" onClick={handleAdd}>
            Add Error Message
          </Button>
        </Box>
      </Stack>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ whiteSpace: 'nowrap' }}>
                  <TableCell>Code</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Subject Trans Code</TableCell>
                  <TableCell>Fallback</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorMessages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No error messages found
                    </TableCell>
                  </TableRow>
                )}

                {page.map((errorMessage, index) => (
                  <TableRow
                    hover
                    key={`message_${errorMessage.id}_${index}`}
                    onClick={() => errorMessageDetails(errorMessage)}
                  >
                    <TableCell>{errorMessage.error_code}</TableCell>
                    <TableCell>{errorMessage.error_level}</TableCell>
                    <TableCell title={errorMessage.subject_trans_code}>
                      {errorMessage.subject_trans_code}
                    </TableCell>
                    <TableCell>{errorMessage.fallback}</TableCell>
                    <TableCell title={errorMessage.error_text}>
                      {errorMessage.error_text}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Paginator
            itemCount={count}
            pageNumber={pageNumber}
            pageLength={pageLength}
            changeEvent={changeEvent}
            editFunction={changePageLength}
          />

          <EditModal
            show={showEditModal}
            onHide={() => setShowEditModal(false)}
            errorMessage={selectedErrorMessage}
            onSave={handleSave}
            errorHandler={errorHandler}
          />
        </>
      )}
    </Box>
  );
};

export default ErrorMessages;
